(function () {
 // Get configuration from script tag data attributes
 const scriptTag = document.currentScript || document.querySelector('script[src*="queueit-integration"]');
 const config = {
   eventName: scriptTag?.dataset.eventName || '',
   acceleratedCheckoutClass: scriptTag?.dataset.acceleratedCheckoutClass || 'shopify-accelerated-checkout',
   paymentButtonSelector: scriptTag?.dataset.paymentButtonSelector || '[data-shopify="payment-button"]'
 };
 
 // Line-item property key used to carry the queue token on each cart line.
 // Leading underscore keeps it hidden from customer-facing surfaces (cart page,
 // checkout summary, thank-you page, order emails). Still visible in Admin.
 const QUEUE_PROPERTY_KEY = '_queue_it';
 
 // Event name tag stored on each cart line for correlation. Also underscore-
 // prefixed so it stays hidden from the customer while remaining visible to
 // Admin/order webhooks.
 const EVENT_NAME_PROPERTY_KEY = '_event_name';
 
 const ADD_ENDPOINTS = ['/cart/add.js', '/cart/add'];
 
 // ---------- helpers ----------
 
 function getEventName() {
   if (config.eventName && config.eventName.trim() !== '') {
     return config.eventName;
   }
   return null;
 }
 
 function getCookie(eventName) {
   const name = 'QueueITAccepted-SDFrts345E-V3_' + eventName;
   const value = `; ${document.cookie}`;
   const parts = value.split(`; ${name}=`);
   if (parts.length === 2) return parts.pop().split(';').shift();
   return null;
 }
 
 function hidePaymentButton() {
   const acceleratedCheckout = document.querySelector(config.acceleratedCheckoutClass);
   if (acceleratedCheckout) {
     acceleratedCheckout.style.display = 'none';
     return;
   }
   const paymentButton = document.querySelector(config.paymentButtonSelector);
   if (paymentButton) {
     paymentButton.style.display = 'none';
   }
 }
 
 function getHeader(headers, name) {
   if (!headers) return null;
   if (headers instanceof Headers) return headers.get(name);
   if (typeof headers === 'object') {
     for (const k of Object.keys(headers)) {
       if (k.toLowerCase() === name.toLowerCase()) return headers[k];
     }
   }
   return null;
 }
 
 function setHeader(init, name, value) {
   if (init.headers instanceof Headers) {
     init.headers.set(name, value);
   } else {
     init.headers = init.headers || {};
     init.headers[name] = value;
   }
 }
 
 // Normalize the outgoing body into one of: {type:'json'|'form'|'formdata'|'urlsearchparams', data}
 function parseBody(body, contentType) {
   if (body == null) return null;
   if (body instanceof FormData) {
     return { type: 'formdata', data: body };
   }
   if (body instanceof URLSearchParams) {
     return { type: 'urlsearchparams', data: body };
   }
   if (typeof body === 'string') {
     const ct = (contentType || '').toLowerCase();
     if (ct.includes('application/json')) {
       try { return { type: 'json', data: JSON.parse(body) }; } catch { return null; }
     }
     // Best-effort: treat as form-encoded
     try { return { type: 'form', data: new URLSearchParams(body) }; } catch { return null; }
   }
   return null;
 }
 
 // Return { id, quantity } for a single-item /cart/add payload (JSON or form-encoded).
 function extractAddParams(parsed) {
   if (!parsed) return null;
   if (parsed.type === 'json') {
     const d = parsed.data;
     if (Array.isArray(d.items) && d.items.length) {
       return { id: String(d.items[0].id), quantity: Number(d.items[0].quantity ?? 1) };
     }
     if (d.id != null) return { id: String(d.id), quantity: Number(d.quantity ?? 1) };
     return null;
   }
   const id = parsed.data.get('id');
   if (id == null) return null;
   return { id: String(id), quantity: Number(parsed.data.get('quantity') ?? 1) };
 }
 
 // Inject line-item properties into the outgoing body, preserving format.
 // `props` is a plain object of key/value pairs to merge into properties.
 function injectQueueProperties(parsed, props) {
   if (!parsed) return null;
   const entries = Object.entries(props || {});
   if (entries.length === 0) return null;
 
   if (parsed.type === 'json') {
     const d = parsed.data;
     if (Array.isArray(d.items)) {
       d.items = d.items.map(item => ({
         ...item,
         properties: { ...(item.properties || {}), ...props }
       }));
     } else {
       d.properties = { ...(d.properties || {}), ...props };
     }
     return JSON.stringify(d);
   }
   for (const [key, value] of entries) {
     parsed.data.set(`properties[${key}]`, value);
   }
   if (parsed.type === 'form') return parsed.data.toString();
   return parsed.data; // FormData / URLSearchParams objects go back as-is
 }
 
 // Build an error Response matching the shape /cart/add.js returns on failure.
 function buildBlockedResponse(message) {
   return new Response(
     JSON.stringify({ status: 422, message, description: message }),
     { status: 422, statusText: 'Unprocessable Entity', headers: { 'Content-Type': 'application/json; charset=utf-8' } }
   );
 }
 
 // ---------- fetch interception ----------
 
 const originalFetch = window.fetch;
 
 window.fetch = async function (input, init) {
   const url = typeof input === 'string' ? input : (input?.url || '');
   const isAdd = ADD_ENDPOINTS.some(ep => url.includes(ep));
   if (!isAdd) {
     return originalFetch.apply(this, arguments);
   }
 
   const eventName = getEventName();
   if (!eventName) {
     // No event configured for this page — behave normally.
     return originalFetch.apply(this, arguments);
   }
 
   const token = getCookie(eventName);
   if (!token) {
     console.warn('[Queue-it] Blocking add-to-cart: no queue token for event', eventName);
     return buildBlockedResponse('Please join the queue for this event before adding this item to your cart.');
   }
 
   // Handle Request-object inputs by cloning and moving body/headers into init.
   if (typeof Request !== 'undefined' && input instanceof Request) {
     const cloned = input.clone();
     init = init || {};
     init.method = init.method || cloned.method;
     if (!init.headers) init.headers = cloned.headers;
     if (init.body == null && cloned.method !== 'GET' && cloned.method !== 'HEAD') {
       init.body = await cloned.text();
     }
     input = cloned.url;
   } else {
     init = init || {};
   }
 
   setHeader(init, 'X-QUEUEIT-EVENT-NAME', eventName);
 
   const parsed = parseBody(init.body, getHeader(init.headers, 'Content-Type'));
   const newBody = injectQueueProperties(parsed, {
     [QUEUE_PROPERTY_KEY]: token,
     [EVENT_NAME_PROPERTY_KEY]: eventName,
   });
   if (newBody != null) {
     init.body = newBody;
   }
   return originalFetch.call(this, input, init);
 };
 
 // ---------- XMLHttpRequest interception ----------
 // Some themes/apps (older Dawn variants, jQuery.ajax callers, third-party
 // quick-add widgets, Shopify's legacy productForm.js) still submit /cart/add
 // via XMLHttpRequest instead of fetch, so we mirror the fetch logic here.
 
 const XHRProto = XMLHttpRequest.prototype;
 const originalXhrOpen = XHRProto.open;
 const originalXhrSend = XHRProto.send;
 const originalXhrSetRequestHeader = XHRProto.setRequestHeader;
 
 XHRProto.open = function (method, url) {
   this.__queueIt = {
     url: String(url || ''),
     method: String(method || 'GET'),
     contentType: null
   };
   return originalXhrOpen.apply(this, arguments);
 };
 
 XHRProto.setRequestHeader = function (name, value) {
   if (this.__queueIt && name && String(name).toLowerCase() === 'content-type') {
     this.__queueIt.contentType = String(value);
   }
   return originalXhrSetRequestHeader.apply(this, arguments);
 };
 
 XHRProto.send = function (body) {
   const ctx = this.__queueIt;
   const url = ctx ? ctx.url : '';
   const isAdd = ADD_ENDPOINTS.some(ep => url.includes(ep));
   if (!isAdd) {
     return originalXhrSend.apply(this, arguments);
   }
 
   const eventName = getEventName();
   if (!eventName) {
     return originalXhrSend.apply(this, arguments);
   }
 
   const token = getCookie(eventName);
   if (!token) {
     console.warn('[Queue-it] Blocking XHR add-to-cart: no queue token for event', eventName);
     simulateXhrBlockedResponse(this, 'Please join the queue for this event before adding this item to your cart.');
     return;
   }
 
   try {
     originalXhrSetRequestHeader.call(this, 'X-QUEUEIT-EVENT-NAME', eventName);
   } catch (err) {
     // Header rejected (e.g. XHR already in an invalid state); ignore.
   }
 
   const parsed = parseBody(body, ctx ? ctx.contentType : null);
   const newBody = injectQueueProperties(parsed, {
     [QUEUE_PROPERTY_KEY]: token,
     [EVENT_NAME_PROPERTY_KEY]: eventName,
   });
   return originalXhrSend.call(this, newBody != null ? newBody : body);
 };
 
 // Synthesize a completed 422 response on an XHR that we chose to block.
 // We override the readonly properties on the instance and fire the standard
 // callback + event pairs asynchronously so callers see normal XHR semantics.
 function simulateXhrBlockedResponse(xhr, message) {
   const payload = JSON.stringify({ status: 422, message, description: message });
   const define = (name, value) => {
     try {
       Object.defineProperty(xhr, name, { configurable: true, get: () => value });
     } catch (err) {
       // Some UA versions disallow redefining these; the caller falls back to
       // whatever the native XHR reports (readyState 1, status 0).
     }
   };
   define('readyState', 4);
   define('status', 422);
   define('statusText', 'Unprocessable Entity');
   define('responseText', payload);
   define('response', xhr.responseType === 'json' ? JSON.parse(payload) : payload);
   define('responseURL', xhr.__queueIt ? xhr.__queueIt.url : '');
 
   xhr.getResponseHeader = (name) =>
     name && String(name).toLowerCase() === 'content-type'
       ? 'application/json; charset=utf-8'
       : null;
   xhr.getAllResponseHeaders = () => 'content-type: application/json; charset=utf-8\r\n';
 
   setTimeout(() => {
     const fire = (type, handler) => {
       try { if (typeof handler === 'function') handler.call(xhr, new Event(type)); }
       catch (err) { console.error(err); }
       try { xhr.dispatchEvent(new Event(type)); } catch (err) { /* older browsers */ }
     };
     fire('readystatechange', xhr.onreadystatechange);
     fire('load', xhr.onload);
     fire('loadend', xhr.onloadend);
   }, 0);
 }
 
 // ---------- post-checkout cookie cleanup ----------
 
 // Signal cookie name prefix set by the Web Pixel on `checkout_completed`.
 // Value format: `${eventName}|${checkoutToken}` (URL-encoded).
 const SIGNAL_COOKIE_PREFIX = 'shopify_e_';
 
 function deleteCookie(name) {
   // Overwrite on both root and current path to cover cookies scoped either way.
   document.cookie = `${name}=; Max-Age=0; Path=/`;
   document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
 }
 
 function cleanupSignaledQueueCookies() {
   if (!document.cookie) return;
 
   for (const pair of document.cookie.split(';')) {
     const trimmed = pair.trim();
     if (!trimmed.startsWith(SIGNAL_COOKIE_PREFIX)) continue;
 
     const eqIdx = trimmed.indexOf('=');
     if (eqIdx <= 0) continue;
 
     const cookieName = trimmed.substring(0, eqIdx);
     const rawValue = trimmed.substring(eqIdx + 1);
     const eventName = cookieName.substring(SIGNAL_COOKIE_PREFIX.length);
     if (!eventName) continue;
 
     // Weak validation: value must be `${eventName}|${nonEmptyToken}`.
     // Prevents a rogue script from planting `shopify_e_hugesale=` to force
     // a cleanup on someone else's browser. For stronger validation, POST
     // the token to an app-proxy endpoint and confirm it maps to a real
     // recent order before deleting.
     let decoded = '';
     try { decoded = decodeURIComponent(rawValue); } catch (_) { continue; }
     const [valueEvent, checkoutToken] = decoded.split('|');
     if (valueEvent !== eventName || !checkoutToken) continue;
 
     console.log('[Queue-it] Deleting signal cookie for event:', eventName);
     deleteCookie(cookieName);
   }
 }
 
 // ---------- page-load bootstrap ----------
 
 function initializeOnPageLoad() {
   cleanupSignaledQueueCookies();
   const eventName = getEventName();
   if (!eventName) return;
   hidePaymentButton();
 }
 
 if (document.readyState === 'complete') {
   initializeOnPageLoad();
 } else {
   window.addEventListener('load', initializeOnPageLoad);
 }
 
 console.log('[Queue-it] Interception script loaded.');
 })();